import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MEAL_TYPES,
  availableUnits,
  resolveBaseAmount,
  scaleNutrients,
  type CanonicalUnit,
  type CreateEntryInput,
  type MealType,
  type Nutrients,
  type NutritionBasis,
  type Serving,
} from '@nutrigeist/shared';
import { diaryApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { MEAL_LABELS, formatGrams, formatKcal } from '../lib/format.js';
import { CloseIcon } from './icons.js';

/**
 * A food ready to be logged, normalised from any of its sources: a catalog
 * entry, a barcode hit, a scanned label or a parsed sentence.
 */
export interface LoggableFood {
  /** Catalog id, or null when the food only exists in this draft. */
  id: string | null;
  name: string;
  brand: string | null;
  basis: NutritionBasis;
  nutrients: Nutrients;
  serving: Serving | null;
  density: number | null;
  /**
   * Barcode to attach if this draft is saved to the catalog. Set by the
   * barcode -> label handoff so a product OpenFoodFacts did not know becomes
   * locally resolvable by that barcode next time.
   */
  barcode?: string | null;
}

interface LogFoodSheetProps {
  food: LoggableFood;
  initialQuantity?: { amount: number; unit: CanonicalUnit } | null;
  /** Warnings from a label scan, shown above the values. */
  warnings?: string[];
  onClose: () => void;
  onLogged: () => void;
  /**
   * Overrides what confirming does. Without it the sheet writes a diary entry;
   * with it the caller decides — the recipe editor collects an ingredient and
   * the Today screen saves an edit, both reusing the same unit handling and
   * live macro preview.
   */
  onSubmit?: (
    food: LoggableFood,
    quantity: { amount: number; unit: CanonicalUnit },
    mealType: MealType,
  ) => void | Promise<void>;
  submitLabel?: string;
  /** Preselects the meal, e.g. when re-adding yesterday's breakfast. */
  initialMealType?: MealType;
}

const UNIT_LABELS: Record<CanonicalUnit, string> = {
  g: 'g',
  ml: 'ml',
  piece: 'Stück',
  serving: 'Portion',
};

/** Suggests a sensible meal from the time of day. */
function currentMeal(): MealType {
  const hour = new Date().getHours();
  if (hour < 10) return 'breakfast';
  if (hour < 15) return 'lunch';
  if (hour < 21) return 'dinner';
  return 'snack';
}

export function LogFoodSheet({
  food,
  initialQuantity,
  warnings,
  onClose,
  onLogged,
  onSubmit,
  submitLabel,
  initialMealType,
}: LogFoodSheetProps): JSX.Element {
  const queryClient = useQueryClient();
  const units = availableUnits(food);

  const defaultUnit: CanonicalUnit =
    initialQuantity?.unit ?? (food.basis === 'per_100ml' ? 'ml' : 'g');

  const [amount, setAmount] = useState(String(initialQuantity?.amount ?? 100));
  const [unit, setUnit] = useState<CanonicalUnit>(defaultUnit);
  const [mealType, setMealType] = useState<MealType>(initialMealType ?? currentMeal);
  // A barcode carried in from a label scan is only useful once persisted, so
  // saving to the catalog is preselected in that case.
  const [saveToCatalog, setSaveToCatalog] = useState(Boolean(food.barcode));
  const [error, setError] = useState<string | null>(null);

  /**
   * The one-tap amounts for this food.
   *
   * The basis unit leads at 100, which is what every nutrition table states and
   * therefore the least surprising default. A declared serving — from the
   * barcode lookup or a scanned label — is offered beside it with its actual
   * weight spelled out, since "1 Portion" means nothing without the grams.
   */
  const quickPicks = useMemo(() => {
    const basisUnit: CanonicalUnit = food.basis === 'per_100ml' ? 'ml' : 'g';
    const picks: { label: string; amount: number; unit: CanonicalUnit }[] = [
      { label: `100 ${basisUnit}`, amount: 100, unit: basisUnit },
      { label: `50 ${basisUnit}`, amount: 50, unit: basisUnit },
    ];

    if (food.serving && food.serving.grams > 0) {
      const grams = Math.round(food.serving.grams * 10) / 10;
      picks.push({
        label: `1 Portion (${formatGrams(grams)} ${basisUnit})`,
        amount: 1,
        unit: 'serving',
      });
    }

    return picks;
  }, [food.basis, food.serving]);

  // Live preview using the same arithmetic the server will apply.
  const preview = useMemo(() => {
    const parsed = Number(amount.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) return null;

    try {
      const baseAmount = resolveBaseAmount(food, { amount: parsed, unit });
      return {
        baseAmount,
        totals: scaleNutrients(food.nutrients, baseAmount / 100),
      };
    } catch {
      // A QuantityError just means this unit does not apply to this food
      // (a piece count with no serving size); the preview stays empty and the
      // save button is disabled until the unit is changed.
      return null;
    }
  }, [amount, unit, food]);

  const logEntry = useMutation({
    mutationFn: async () => {
      const parsed = Number(amount.replace(',', '.'));
      if (!Number.isFinite(parsed) || parsed <= 0) {
        throw new Error('Bitte gib eine gültige Menge ein.');
      }

      if (onSubmit) {
        await onSubmit(food, { amount: parsed, unit }, mealType);
        return null;
      }

      const entry: CreateEntryInput = {
        food:
          food.id != null
            ? { foodId: food.id }
            : {
                name: food.name,
                brand: food.brand,
                basis: food.basis,
                nutrients: food.nutrients,
                serving: food.serving,
                density: food.density,
                saveToCatalog,
                // Only meaningful together with saveToCatalog; the server
                // ignores it otherwise.
                barcode: food.barcode ?? undefined,
              },
        quantity: { amount: parsed, unit },
        mealType,
      };

      return diaryApi.createEntries([entry], `${parsed} ${unit} ${food.name}`);
    },
    onSuccess: async (result) => {
      // A null result means `onSubmit` handled it and no diary entry was made,
      // so there is nothing to invalidate.
      if (result) await queryClient.invalidateQueries({ queryKey: ['diary'] });
      onLogged();
    },
    onError: (caught) => {
      setError(caught instanceof ApiError ? caught.message : (caught as Error).message);
    },
  });

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${food.name} erfassen`}
      >
        <div className="sheet-handle" />

        <div className="row-between" style={{ marginBottom: 12 }}>
          <div className="grow">
            <h2>{food.name}</h2>
            <span className="subtitle">
              {food.brand}
              {food.brand && food.serving ? ' · ' : ''}
              {/* The label the product printed ("1 Riegel", "30 g") next to what
                  it actually weighs, so the serving option is self-explaining. */}
              {food.serving
                ? `${food.serving.label} = ${formatGrams(food.serving.grams)} ${
                    food.basis === 'per_100ml' ? 'ml' : 'g'
                  }`
                : ''}
            </span>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Schließen">
            <CloseIcon />
          </button>
        </div>

        {warnings && warnings.length > 0 && (
          <div className="banner banner-warning" style={{ marginBottom: 12 }} role="alert">
            <strong>Bitte prüfen:</strong>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="stack">
          {/* One tap for the amounts people actually use. 100 g stays the
              default and the first chip, because it is the basis every
              nutrition table is stated in; the serving is offered next to it
              when the product declared one. */}
          <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
            {quickPicks.map((pick) => {
              const active = unit === pick.unit && amount === String(pick.amount);
              return (
                <button
                  key={pick.label}
                  type="button"
                  className={active ? 'btn btn-primary btn-sm' : 'btn btn-ghost btn-sm'}
                  onClick={() => {
                    setAmount(String(pick.amount));
                    setUnit(pick.unit);
                  }}
                >
                  {pick.label}
                </button>
              );
            })}
          </div>

          <div className="row" style={{ alignItems: 'flex-end' }}>
            <div className="field grow">
              <label htmlFor="amount">Menge</label>
              <input
                id="amount"
                className="input"
                inputMode="decimal"
                autoFocus
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
              />
            </div>

            <div className="field" style={{ width: 120 }}>
              <label htmlFor="unit">Einheit</label>
              <select
                id="unit"
                className="select"
                value={unit}
                onChange={(event) => setUnit(event.target.value as CanonicalUnit)}
              >
                {units.map((value) => (
                  <option key={value} value={value}>
                    {UNIT_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="field">
            <label htmlFor="meal">Mahlzeit</label>
            <select
              id="meal"
              className="select"
              value={mealType}
              onChange={(event) => setMealType(event.target.value as MealType)}
            >
              {MEAL_TYPES.map((value) => (
                <option key={value} value={value}>
                  {MEAL_LABELS[value]}
                </option>
              ))}
            </select>
          </div>

          {food.id == null && !onSubmit && (
            <label className="row" style={{ gap: 8 }}>
              <input
                type="checkbox"
                checked={saveToCatalog}
                onChange={(event) => setSaveToCatalog(event.target.checked)}
              />
              <span>
                Als eigenes Lebensmittel speichern
                {food.barcode ? ' (Barcode wird hinterlegt)' : ''}
              </span>
            </label>
          )}

          <div className="card card-tight">
            {preview ? (
              <>
                <div className="calorie-headline">
                  <span className="value">{formatKcal(preview.totals.kcal)}</span>
                  <span className="unit">kcal</span>
                </div>
                <div className="macro-grid">
                  <div className="macro-cell">
                    <div className="macro-value">{formatGrams(preview.totals.protein)} g</div>
                    <div className="macro-label">Eiweiß</div>
                  </div>
                  <div className="macro-cell">
                    <div className="macro-value">{formatGrams(preview.totals.carbs)} g</div>
                    <div className="macro-label">Kohlenh.</div>
                  </div>
                  <div className="macro-cell">
                    <div className="macro-value">{formatGrams(preview.totals.fat)} g</div>
                    <div className="macro-label">Fett</div>
                  </div>
                </div>
              </>
            ) : (
              <span className="hint">Menge eingeben, um die Nährwerte zu berechnen.</span>
            )}
          </div>

          {error && (
            <div className="banner banner-error" role="alert">
              {error}
            </div>
          )}

          <button
            className="btn btn-primary btn-block"
            disabled={logEntry.isPending || !preview}
            onClick={() => logEntry.mutate()}
          >
            {logEntry.isPending ? <span className="spinner" /> : (submitLabel ?? 'Eintragen')}
          </button>
        </div>
      </div>
    </div>
  );
}
